import { LayoutModule } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Route, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { UserIdleModule } from 'angular-user-idle';
import { AppComponent } from 'app/app.component';
import { CoreLayoutModule } from 'app/core/layout/layout.module';
import { CoreStoreModule } from 'app/core/store/store.module';
import { CoreAuthenticationModule } from 'app/shared/authentication/authentication.module';
import { AuthGuard } from 'app/shared/authentication/guards/auth.guard';
import { ClientHttpService } from 'app/shared/clients/services/client-http.service';
import { SharedDynamicModule } from 'app/shared/dynamic/dynamic.module';
import { WINDOW_PROVIDERS } from 'app/shared/layout/services/window-provider.service';
import { WindowRef } from 'app/shared/layout/services/window-ref.service';
import { SharedPhoneModule } from 'app/shared/phone/phone.module';
import { SharedPopupModule } from 'app/shared/popup/popup.module';
import { WebPartnerAuditHttpService } from 'app/shared/portal/services/web-partner-audit-http.service';
import { AppHttpInterceptor } from 'app/shared/requests/interceptors/app-http-interceptor';
import { ErrorInterceptorProvider } from 'app/shared/requests/interceptors/error.interceptor';
import {
  DxDropDownButtonModule,
  DxProgressBarModule,
  DxScrollViewModule,
  DxTreeViewModule,
} from 'devextreme-angular';
import { environment } from 'environments/environment';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { PortalSetupHttpService } from './shared/portal/services/portal-setup-http.service';
import { LocalStorageService } from './shared/storage/services/local-storage.service';
import { CoreTermsAndConditionsModule } from './shared/terms-and-conditions/terms-and-conditions.module';
import { ErrorHandlingTestComponent } from './components/error-handling-test/error-handling-test.component';
import { NavigationHandlerService } from './shared/authentication/services/navigation-handler.service';

const routes: Route[] = [
  {
    path: '',
    loadChildren: () => import('./modules/referrer/referrer.module').then(m => m.ReferrerModule),
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'error/:code',
    component: ErrorHandlingTestComponent,
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];

// required for AOT compilation
export function translateHttpLoaderFactory(httpBackend: HttpBackend): TranslateHttpLoader {
  return new TranslateHttpLoader(new HttpClient(httpBackend));
}

const dbConfig: DBConfig = {
  name: 'ClinicMasterDB',
  version: 1,
  objectStoresMeta: [
    {
      store: 'ChartNotes',
      storeConfig: {
        keyPath: '',
        autoIncrement: false,
      },
      storeSchema: [],
    },
  ],
};

@NgModule({
  declarations: [AppComponent, ErrorHandlingTestComponent],
  bootstrap: [AppComponent],
  imports: [
    CoreAuthenticationModule,
    CoreTermsAndConditionsModule,
    SharedDynamicModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    CoreStoreModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        deps: [HttpBackend],
        useFactory: translateHttpLoaderFactory,
      },
      extend: true,
    }),
    LayoutModule,
    CoreLayoutModule,
    DxScrollViewModule,
    SharedPopupModule,
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      enableTracing: false,
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
    }),
    // Optionally you can set time for `idle`, `timeout` and `ping` in seconds.
    // Default values: `idle` is 600 (10 minutes), `timeout` is 300 (5 minutes)
    // and `ping` is 120 (2 minutes).
    UserIdleModule.forRoot({ idle: 600, timeout: 300, ping: 120 }),
    NgxIndexedDBModule.forRoot(dbConfig),
    DxProgressBarModule,
    DxTreeViewModule,
    DxDropDownButtonModule,
    SharedPhoneModule,
  ],
  providers: [
    ErrorInterceptorProvider,
    WINDOW_PROVIDERS,
    AuthGuard,
    WindowRef,
    WebPartnerAuditHttpService,
    ClientHttpService,
    { provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
    PortalSetupHttpService,
    LocalStorageService,
    provideHttpClient(withInterceptorsFromDi()),
    NavigationHandlerService,
  ],
})
export class AppModule {}
